/* ==========================================================================
   SILAF — one small vanilla script. Everything works without it; this enhances.
   Header · menu · search · cart drawer (Section Rendering API) · variants · qty ·
   sticky purchase bar · reveal · strap drift.  (§4.5, §5.1, §5.16, §6.2, §11, §12)
   ========================================================================== */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const cfg = window.Silaf || { routes: {}, strings: {} };

  /* ---------- Focus trap for drawer / menu / search (§11) ---------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  let trap = null;
  function openLayer(el, opener, onClose) {
    closeLayer();
    el.hidden = false;
    el.removeAttribute('inert');
    void el.offsetWidth; // commit the closed state so the slide-in transition runs
    el.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    // Select the backdrop by class: layers also carry data-scrim to say whether they want one,
    // and the menu panel (data-scrim="none") precedes the backdrop in the DOM, so an attribute
    // lookup returned the menu and unhid it whenever the cart opened.
    const scrim = $('.scrim');
    if (scrim && el.dataset.scrim !== 'none') scrim.hidden = false;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); closeLayer(); return; }
      if (e.key !== 'Tab') return;
      const items = $$(FOCUSABLE, el).filter((n) => n.offsetParent !== null || n === document.activeElement);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    trap = { el, opener, onKey, onClose };
    if (opener) opener.setAttribute('aria-expanded', 'true');
    const target = $('[data-autofocus]', el) || $$(FOCUSABLE, el)[0];
    if (target) target.focus({ preventScroll: true });
  }
  function closeLayer() {
    if (!trap) return;
    const { el, opener, onKey, onClose } = trap;
    trap = null;
    el.classList.remove('is-open');
    document.removeEventListener('keydown', onKey);
    document.documentElement.style.overflow = '';
    const scrim = $('.scrim');
    if (scrim) scrim.hidden = true;
    const hide = () => { el.hidden = true; };
    if (el.classList.contains('drawer') && !reduceMotion) setTimeout(hide, 330); else hide();
    if (opener) { opener.setAttribute('aria-expanded', 'false'); opener.focus(); }
    if (onClose) onClose();
  }
  document.addEventListener('click', (e) => {
    if (e.target.closest('.scrim') || e.target.closest('[data-close-layer]')) { e.preventDefault(); closeLayer(); }
  });

  /* ---------- Header: sticky rule after scroll (§5.1) ---------- */
  const header = $('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-stuck', window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Mobile menu + search panel ---------- */
  document.addEventListener('click', (e) => {
    const menuBtn = e.target.closest('[data-open-menu]');
    if (menuBtn) { e.preventDefault(); openLayer($('#MenuPanel'), menuBtn); return; }
    const searchBtn = e.target.closest('[data-open-search]');
    if (searchBtn) { e.preventDefault(); openLayer($('#SearchPanel'), searchBtn); return; }
    const cartBtn = e.target.closest('[data-open-cart]');
    if (cartBtn && cfg.cartType === 'drawer' && $('#CartDrawer')) { e.preventDefault(); openLayer($('#CartDrawer'), cartBtn); }
  });


  /* ---------- Default hero gallery: thumbnails swap the main image in place ---------- */
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-gallery-thumb]');
    if (!thumb) return;
    const stage = document.querySelector('[data-gallery-stage] img');
    if (!stage) return;
    stage.src = thumb.dataset.full;
    if (thumb.dataset.srcset) stage.srcset = thumb.dataset.srcset;
    $$('[data-gallery-thumb]').forEach((t) => t.classList.toggle('is-active', t === thumb));
  });

  /* ---------- Identity strap: drifts by itself, drag to scrub (§7.1.4) ----------
     The band repeats horizontally, so the offset only ever needs to wrap within one tile width.
     Drift honours prefers-reduced-motion; dragging always works, since that is user-initiated. */
  $$('[data-strap-scrub]').forEach((band) => {
    const speed = Number(band.dataset.speed || 12);
    let offset = 0, dragging = false, startX = 0, startOffset = 0, raf = null, last = 0;

    const apply = () => band.style.setProperty('--band-offset', `${offset}px`);

    const tick = (t) => {
      if (!last) last = t;
      const dt = Math.min(64, t - last);
      last = t;
      if (!dragging && speed > 0) { offset -= (speed * dt) / 1000; apply(); }
      raf = requestAnimationFrame(tick);
    };
    if (speed > 0 && !reduceMotion) raf = requestAnimationFrame(tick);

    band.addEventListener('pointerdown', (e) => {
      dragging = true;
      startX = e.clientX;
      startOffset = offset;
      band.setPointerCapture(e.pointerId);
    });
    band.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      offset = startOffset + (e.clientX - startX);
      apply();
    });
    const stop = (e) => {
      if (!dragging) return;
      dragging = false;
      if (e.pointerId != null && band.hasPointerCapture(e.pointerId)) band.releasePointerCapture(e.pointerId);
    };
    band.addEventListener('pointerup', stop);
    band.addEventListener('pointercancel', stop);

    band.addEventListener('keydown', (e) => {
      const step = e.shiftKey ? 120 : 40;
      if (e.key === 'ArrowLeft') { offset += step; apply(); e.preventDefault(); }
      if (e.key === 'ArrowRight') { offset -= step; apply(); e.preventDefault(); }
    });
  });

  /* ---------- Sookoo XII: drag the sun disc (product page easter egg) ---------- */
  $$('[data-disc-spin]').forEach((el) => {
    let angle = 0, dragging = false, lastA = 0, raf = null, prev = 0;
    const centre = () => { const r = el.getBoundingClientRect(); return { x: r.left + r.width / 2, y: r.top + r.height / 2 }; };
    const angleAt = (e) => { const c = centre(); return Math.atan2(e.clientY - c.y, e.clientX - c.x) * 180 / Math.PI; };
    const apply = () => el.style.setProperty('--disc-angle', angle.toFixed(2) + 'deg');

    const tick = (t) => {
      if (!prev) prev = t;
      const dt = Math.min(64, t - prev); prev = t;
      if (!dragging) { angle += (3 * dt) / 1000; apply(); }
      raf = requestAnimationFrame(tick);
    };
    if (!reduceMotion) raf = requestAnimationFrame(tick);

    el.addEventListener('pointerdown', (e) => {
      dragging = true; lastA = angleAt(e); el.setPointerCapture(e.pointerId);
    });
    el.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const a = angleAt(e);
      let d = a - lastA;
      if (d > 180) d -= 360; else if (d < -180) d += 360;  // shortest way round
      angle += d; lastA = a; apply();
    });
    const stop = (e) => {
      if (!dragging) return;
      dragging = false;
      if (e.pointerId != null && el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
    };
    el.addEventListener('pointerup', stop);
    el.addEventListener('pointercancel', stop);
    el.addEventListener('keydown', (e) => {
      const step = e.shiftKey ? 30 : 10;
      if (e.key === 'ArrowLeft') { angle -= step; apply(); e.preventDefault(); }
      if (e.key === 'ArrowRight') { angle += step; apply(); e.preventDefault(); }
    });
  });

  /* ---------- Brew timer: the steps with timestamps become a clock ---------- */
  $$('[data-brew-timer]').forEach((timer) => {
    const root = timer.closest('.brew');
    const steps = $$('[data-brew-step]', root);
    if (!steps.length) return;                       // no timed steps, no timer
    timer.hidden = false;

    const toSeconds = (s) => {
      const [m, sec] = String(s).split(':').map(Number);
      return (m || 0) * 60 + (sec || 0);
    };
    const marks = steps.map((el) => ({ el, at: toSeconds(el.dataset.at) })).sort((a, b) => a.at - b.at);
    const elapsedEl = $('[data-brew-elapsed]', timer);
    const nowEl = $('[data-brew-now]', timer);
    const startBtn = $('[data-brew-start]', timer);
    const resetBtn = $('[data-brew-reset]', timer);

    let t0 = 0, raf = null, running = false;
    const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

    function paint(sec) {
      elapsedEl.textContent = fmt(sec);
      let current = null;
      marks.forEach((m) => {
        m.el.classList.toggle('is-done', sec > m.at);
        m.el.classList.remove('is-now');
        if (sec >= m.at) current = m;
      });
      if (current) {
        current.el.classList.add('is-now');
        current.el.classList.remove('is-done');
        nowEl.textContent = ($('.brew__text', current.el) || {}).textContent?.slice(0, 60) || '';
      } else {
        nowEl.textContent = marks.length ? `Next at ${fmt(marks[0].at)}` : '';
      }
    }

    function tick() {
      paint((Date.now() - t0) / 1000);
      raf = requestAnimationFrame(tick);
    }

    startBtn.addEventListener('click', () => {
      if (running) {
        running = false;
        cancelAnimationFrame(raf);
        startBtn.textContent = 'Resume';
        return;
      }
      t0 = Date.now() - (toSeconds(elapsedEl.textContent) * 1000);
      running = true;
      startBtn.textContent = 'Pause';
      tick();
    });

    resetBtn.addEventListener('click', () => {
      running = false;
      cancelAnimationFrame(raf);
      startBtn.textContent = 'Start brew';
      elapsedEl.textContent = '0:00';
      marks.forEach((m) => m.el.classList.remove('is-now', 'is-done'));
      nowEl.textContent = 'Ready';
    });
  });
  /* ---------- Predictive search ---------- */
  const searchInput = $('[data-predictive-input]');
  if (searchInput && cfg.routes.predictiveSearch) {
    const out = $('[data-predictive-results]');
    let timer, ctrl;
    searchInput.addEventListener('input', () => {
      clearTimeout(timer);
      const q = searchInput.value.trim();
      if (q.length < 2) { out.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        if (ctrl) ctrl.abort();
        ctrl = new AbortController();
        try {
          const url = `${cfg.routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,collection,page,article&resources[limit]=6`;
          const res = await fetch(url, { signal: ctrl.signal });
          if (!res.ok) return;
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = $('[data-predictive-inner]', doc);
          out.innerHTML = inner ? inner.innerHTML : '';
        } catch (err) { /* aborted or offline: the form still submits to /search */ }
      }, 180);
    });
  }

  /* ---------- Toast ---------- */
  const toast = $('[data-toast]');
  function say(msg) {
    if (!toast) return;
    toast.textContent = msg;
    toast.hidden = false;
    clearTimeout(say.t);
    say.t = setTimeout(() => { toast.hidden = true; }, 2600);
  }

  /* ---------- Cart (§5.16): add, change, re-render drawer via Section Rendering API ---------- */
  function applyCartSections(sections) {
    if (!sections) return;
    const html = sections['cart-drawer'];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = $('[data-drawer-content]', doc);
    const current = $('#CartDrawer [data-drawer-content]');
    if (fresh && current) current.replaceWith(document.importNode(fresh, true));
    const count = fresh ? fresh.dataset.cartCount : null;
    if (count !== null && count !== undefined) {
      $$('[data-cart-bubble]').forEach((n) => { n.textContent = count; });
      $$('[data-cart-count-label]').forEach((n) => { n.textContent = `Cart, ${count} item${count === '1' ? '' : 's'}`; });
    }
  }

  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: body instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || cfg.strings.error);
    return data;
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form || cfg.cartType !== 'drawer' || !$('#CartDrawer')) return;
    e.preventDefault();
    const buttons = $$(`[type="submit"][form="${form.id}"], [type="submit"]`, document).filter((b) => b.form === form);
    const status = $('[data-form-status]', form.closest('[data-product]') || document);
    buttons.forEach((b) => { b.classList.add('is-loading'); b.setAttribute('aria-disabled', 'true'); });
    if (status) { status.textContent = ''; status.hidden = true; }
    try {
      const fd = new FormData(form);
      fd.append('sections', 'cart-drawer');
      fd.append('sections_url', window.location.pathname);
      const data = await cartRequest(cfg.routes.cartAdd + '.js', fd);
      applyCartSections(data.sections);
      say(cfg.strings.added);
      openLayer($('#CartDrawer'), buttons[0]);
    } catch (err) {
      if (status) { status.textContent = err.message; status.hidden = false; }
    } finally {
      buttons.forEach((b) => { b.classList.remove('is-loading'); b.removeAttribute('aria-disabled'); });
    }
  });

  async function changeLine(line, quantity, focusKey) {
    const drawer = $('#CartDrawer');
    if (drawer) drawer.setAttribute('aria-busy', 'true');
    try {
      const data = await cartRequest(cfg.routes.cartChange + '.js', { line, quantity, sections: ['cart-drawer'], sections_url: window.location.pathname });
      applyCartSections(data.sections);
      const again = focusKey && $(`[data-line-key="${focusKey}"] [data-line-qty]`);
      if (again) again.focus(); else { const f = $('#CartDrawer [data-autofocus]') || $('#CartDrawer .drawer__close'); if (f) f.focus(); }
    } catch (err) { say(err.message); }
    finally { if (drawer) drawer.removeAttribute('aria-busy'); }
  }
  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-line-remove]');
    if (rm && rm.closest('#CartDrawer')) { e.preventDefault(); changeLine(Number(rm.dataset.line), 0); }
  });
  document.addEventListener('change', (e) => {
    const q = e.target.closest('[data-line-qty]');
    if (q && q.closest('#CartDrawer')) changeLine(Number(q.dataset.line), Math.max(0, parseInt(q.value, 10) || 0), q.closest('[data-line-key]')?.dataset.lineKey);
    if (q && q.closest('[data-cart-page]')) q.form?.requestSubmit ? q.form.requestSubmit() : q.form?.submit();
  });

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-qty-step]');
    if (!b) return;
    const input = $('input', b.closest('[data-qty]'));
    if (!input) return;
    const min = Number(input.min || 0), max = input.max ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || 0) + Number(b.dataset.qtyStep)));
    if (next === Number(input.value)) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Variant picker: radios named "id" work natively; this syncs price, stock, URL ---------- */
  $$('[data-product]').forEach((root) => {
    const json = $('[data-variants-json]', root);
    if (!json) return;
    const variants = JSON.parse(json.textContent);
    root.addEventListener('change', (e) => {
      if (!e.target.matches('input[name="id"]')) return;
      const v = variants.find((x) => String(x.id) === e.target.value);
      if (!v) return;
      $$('[data-price-num]', root).forEach((n) => { n.textContent = v.price_text; });
      $$('[data-price-label]', root).forEach((n) => { n.textContent = v.label; });
      $$('[data-add-button]').forEach((b) => {
        b.disabled = !v.available;
        const lbl = $('[data-add-label]', b);
        if (lbl) lbl.textContent = v.available ? b.dataset.labelAvailable : (cfg.strings.soldOut || 'Sold out');
      });
      $$('[data-sticky-variant]').forEach((n) => { n.value = v.id; });
      // Choosing a finish changes the picture. Only when that variant has its own photograph —
      // otherwise the image stays as it is rather than blanking out.
      if (v.image) {
        const stage = $('[data-gallery-stage]', root) || $('[data-gallery-stage]');
        const img = stage && stage.querySelector('img');
        if (img) {
          img.removeAttribute('srcset');
          img.src = v.image;
        }
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url);
    });
  });

  /* ---------- Sticky purchase bar on mobile once the purchase row leaves the viewport (§6.2) ---------- */
  // Position check rather than IntersectionObserver: a jump (anchor, fling, restored scroll) can skip
  // straight past the row without it ever intersecting, and the bar must still appear.
  const row = $('[data-purchase-row]');
  const bar = $('[data-sticky-atc]');
  if (row && bar) {
    let shown = null, queued = false;
    const update = () => {
      queued = false;
      const past = row.getBoundingClientRect().bottom < 0;
      if (past === shown) return;
      shown = past;
      bar.classList.toggle('is-visible', past);
      bar.toggleAttribute('inert', !past);
      bar.setAttribute('aria-hidden', String(!past));
    };
    const queue = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    update();
  }

  /* ---------- Notes/spec accordions start collapsed on phones (§10.3) ---------- */
  if (window.matchMedia('(max-width: 767px)').matches) $$('details[data-desktop-open]').forEach((d) => { d.open = false; });

  /* ---------- Reveal: rise + fade, once (§4.5) ---------- */
  const reveals = $$('[data-reveal]');
  if (reveals.length) {
    if (reduceMotion || !('IntersectionObserver' in window)) reveals.forEach((n) => n.classList.add('is-in'));
    else {
      const io = new IntersectionObserver((entries) => entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
      }), { rootMargin: '0px 0px -6% 0px', threshold: 0.08 });
      reveals.forEach((n) => io.observe(n));
    }
  }

  /* ---------- Strap drift: 20–40px of parallax on ribbons (§4.5) ---------- */
  const drifters = $$('[data-drift]');
  if (drifters.length && !reduceMotion) {
    let ticking = false;
    const frame = () => {
      const vh = window.innerHeight;
      drifters.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -100 || r.top > vh + 100) return;
        const amt = Number(el.dataset.drift) || 30;
        const p = (r.top + r.height / 2 - vh / 2) / vh; // -1..1 around centre
        el.style.setProperty('--drift', `${(-p * amt).toFixed(1)}px`);
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }, { passive: true });
    frame();
  }

})();